/**
 * Where to put a popover so it stays glued to a pin drawn inside the iframe.
 *
 * ============================================================================
 * THE COORDINATE SPACE, AND WHY IT IS THIS ONE
 * ============================================================================
 * `iframe.contentDocument` is `null` across the sandbox's opaque origin, so the
 * parent cannot measure anything inside the prototype. Every number here
 * arrives over `postMessage`: `atlas:pin-positions` reports each marker in
 * **iframe viewport coordinates** — origin at the top-left of the framed
 * document's viewport, which is exactly the iframe element's content box.
 *
 * The overlay this positions is therefore rendered as a sibling of the iframe,
 * inside the frame's own positioning wrapper, so `left`/`top` are that same
 * space with only the frame's border between them ({@link FRAME_BORDER_PX}).
 * That is the entire reason it is placed there rather than portalled to the
 * body: an overlay in the frame's layout box moves with the frame for free —
 * through canvas scrolling, through the 200 ms width-preset transition, through
 * a window resize — with no listener, no measurement and no rAF loop of our
 * own. The only thing that has to be tracked is the pin's position inside the
 * prototype, and the agent already streams that, throttled to one message per
 * animation frame. Anything smoother would be this side fighting that one.
 */

/** The frame's 1px border sits between the wrapper's origin and the iframe's. */
export const FRAME_BORDER_PX = 1;

/** Gap between the pin marker and the popover's near edge. */
const PIN_GAP_PX = 20;

/** Smallest gap kept between the popover and the frame's edges. */
const EDGE_MARGIN_PX = 8;

/**
 * Half the agent's 26px marker, so the popover clears it on the flipped side
 * too. Hard-coded because the marker is drawn by the agent with `!important`
 * inline styles and its size is part of the protocol's visual contract.
 */
const PIN_RADIUS_PX = 13;

export interface PinPoint {
  /** X in iframe viewport coordinates. */
  readonly x: number;
  /** Y in iframe viewport coordinates. */
  readonly y: number;
}

export interface Size {
  readonly width: number;
  readonly height: number;
}

/** Which side of the pin the popover ended up on, for the caller's styling. */
export type PopoverSide = 'left' | 'right';

export interface PopoverPosition {
  /** CSS `left`, relative to the frame wrapper's padding box. */
  readonly left: number;
  /** CSS `top`, relative to the frame wrapper's padding box. */
  readonly top: number;
  readonly side: PopoverSide;
}

const clamp = (value: number, min: number, max: number): number =>
  max < min ? min : Math.min(Math.max(value, min), max);

/**
 * Place a popover beside a pin.
 *
 * Right of the pin by default, because a reviewer reads left to right and the
 * thing being commented on should not end up behind the comment. It flips left
 * only when the popover would not fit on the right *and* genuinely fits on the
 * left — flipping into an equally bad position would just make the popover
 * jump about as the reviewer scrolls.
 *
 * Vertically the popover hangs from the pin rather than centring on it: a
 * thread grows downwards as replies arrive, and a centred popover would slide
 * upwards every time somebody typed.
 *
 * Everything is finally clamped inside the frame, so a pin in a corner produces
 * a popover that is fully readable rather than one half outside the prototype.
 * When the popover is larger than the frame — a long thread at Mobile width —
 * the clamp resolves to the top-left margin and the popover's own scrolling
 * takes over.
 */
export const positionPinPopover = (
  pin: PinPoint,
  popover: Size,
  frame: Size,
): PopoverPosition => {
  const rightLeft = pin.x + PIN_RADIUS_PX + PIN_GAP_PX;
  const leftLeft = pin.x - PIN_RADIUS_PX - PIN_GAP_PX - popover.width;

  const fitsRight = rightLeft + popover.width <= frame.width - EDGE_MARGIN_PX;
  const fitsLeft = leftLeft >= EDGE_MARGIN_PX;

  const side: PopoverSide = fitsRight || !fitsLeft ? 'right' : 'left';
  const rawLeft = side === 'right' ? rightLeft : leftLeft;

  /*
   * Anchored a little above the pin's centre so the popover's header lines up
   * with the marker rather than starting below it.
   */
  const rawTop = pin.y - PIN_RADIUS_PX * 2;

  return {
    left:
      clamp(
        rawLeft,
        EDGE_MARGIN_PX,
        frame.width - popover.width - EDGE_MARGIN_PX,
      ) + FRAME_BORDER_PX,
    top:
      clamp(
        rawTop,
        EDGE_MARGIN_PX,
        frame.height - popover.height - EDGE_MARGIN_PX,
      ) + FRAME_BORDER_PX,
    side,
  };
};

/**
 * Where to draw the parent-side hit target that sits over one pin.
 *
 * The markers themselves are drawn *inside* the prototype, so the parent has no
 * hover or click events for them — `atlas:pin-clicked` exists, but there is no
 * `atlas:pin-hovered` and adding one would be a protocol change. A small
 * transparent target laid over the marker gives the parent both, and is what
 * makes "hovering a pin highlights its panel row" (spec R6.3) possible at all.
 *
 * It is kept to the marker's own diameter. Larger would swallow clicks meant
 * for the prototype underneath; smaller would leave a rim of marker that only
 * the agent can see.
 */
export const PIN_HIT_TARGET_PX = PIN_RADIUS_PX * 2;

export const positionPinHitTarget = (pin: PinPoint): { left: number; top: number } => ({
  left: pin.x - PIN_RADIUS_PX + FRAME_BORDER_PX,
  top: pin.y - PIN_RADIUS_PX + FRAME_BORDER_PX,
});
